import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { LogoIcon } from './Logo';
import LegalGate from './LegalGate';
import { CURRENT_LEGAL_VERSION } from './legal/LegalContent';
import CommandPalette from './CommandPalette';
import FeedbackButton from './FeedbackButton';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { query, where, orderBy, limit, updateDoc, doc } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import { useCollection } from '../hooks/useFirestore';
import { buildUserTodos } from '../lib/todos';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import { IDLE_TIMEOUT_MS, IDLE_WARNING_MS } from '../lib/sessionTimeout';
import { reviewsCol, cyclesCol, notificationsCol, leaveRequestsCol, policiesCol, policyAcksCol } from '../lib/firestore';
import { inAckAudience } from '../lib/policyAudience';
import app, { db } from '../lib/firebase';
import { useSchool } from '../contexts/SchoolContext';
import type { ModuleId, UserRole, AppNotification, Cycle, Policy, PolicyAck, BehaviourFeatureId } from '../types';

interface NavItem {
  label: string;
  to: string;
  roles: UserRole[];
  /** When set, the item only shows if the school has this module */
  module?: ModuleId;
  /** When set, the item only shows if this behaviour/pastoral feature is switched on */
  behaviourFeature?: BehaviourFeatureId;
  /** When set, the item only shows to the SEND team and named leadership. SEND
   *  access is a membership list, not a role, so `roles` cannot express it. */
  sendCompliance?: boolean;
  /** When set, the item only shows to the staff absence viewer list — the same
   *  list-not-role shape as sendCompliance. */
  staffAbsenceViewer?: boolean;
  /** When set, the item only shows if this is the measure the school's
   *  absence policy uses (staffAbsenceConfig.measure). The other page stays
   *  reachable by its address; this keeps the menu to one measure. */
  staffAbsenceMeasure?: 'bradford' | 'reviewPoints';
  /** When set, the item only shows to a named SENCo who is not the school
   *  admin — the admin already has Settings, where the same section lives. */
  sendSenco?: boolean;
  /** Sidebar group. 'pinned'/'platform' render at the top with no header. */
  group: 'pinned' | 'platform' | 'teaching' | 'behaviour' | 'send' | 'staffAbsence' | 'mywork' | 'leadership' | 'school' | 'admin';
}

const ALL_SCHOOL_ROLES: UserRole[] = ['schoolAdmin', 'headteacher', 'lineManager', 'staff'];

/** Collapsible groups, in display order. (pinned + platform are not listed — they render flat at the top.) */
const NAV_GROUPS: { key: NavItem['group']; label: string }[] = [
  { key: 'teaching',   label: 'Teaching' },
  { key: 'behaviour',  label: 'Behaviour & pastoral' },
  { key: 'send',       label: 'SEND' },
  { key: 'staffAbsence', label: 'Staff absence' },
  { key: 'mywork',     label: 'My work' },
  { key: 'leadership', label: 'Leadership' },
  { key: 'school',     label: 'School' },
  { key: 'admin',      label: 'Admin & setup' },
];

const NAV_ITEMS: NavItem[] = [
  // Platform (super admin / trust admin) — render flat at top
  { label: 'Schools',     to: '/admin',             roles: ['superAdmin'], group: 'platform' },
  { label: 'Trusts',      to: '/admin/trusts',      roles: ['superAdmin'], group: 'platform' },
  { label: 'Sign-ins',    to: '/admin/sign-ins',    roles: ['superAdmin'], group: 'platform' },
  { label: 'Feedback',    to: '/admin/feedback',    roles: ['superAdmin'], group: 'platform' },
  { label: 'My Trust',    to: '/trust',             roles: ['trustAdmin'], group: 'platform' },
  // Pinned
  { label: 'Home',        to: '/home',              roles: ALL_SCHOOL_ROLES, group: 'pinned' },
  // Pinned, not under School: finding a pupil is something every member of
  // staff does most days, and the Roster sat in a collapsed group.
  { label: 'Students',    to: '/students',          roles: ALL_SCHOOL_ROLES, group: 'pinned' },
  // To Do is hidden from the nav for now — Home already leads with the same
  // list, so the menu entry was a second route to the same place. The /todo
  // route still works and Home links to it ("Open To Do" / "View all (N)"),
  // which is the only way in while this is commented out. Restore this line to
  // bring back the menu item AND its unread badge, which is the only
  // always-visible count of what needs attention.
  // No module gate — To Do aggregates across modules (PM reviews, leave
  // approvals, …), so it must survive a school not having PM.
  // { label: 'To Do',       to: '/todo',              roles: ALL_SCHOOL_ROLES, group: 'pinned' },
  // Teaching
  { label: 'My Classes',  to: '/my-classes',        roles: ['schoolAdmin', 'headteacher', 'lineManager', 'staff'], group: 'teaching' },
  { label: 'Marking',     to: '/marker',            roles: ALL_SCHOOL_ROLES, module: 'marker', group: 'teaching' },
  // Behaviour & pastoral — one blade per feature; each self-hides when the
  // school admin switches it off (behaviourFeature + activeBehaviourFeatures).
  { label: 'Head Teacher Detentions', to: '/behaviour/detentions', roles: ALL_SCHOOL_ROLES, module: 'behaviour', behaviourFeature: 'detentions', group: 'behaviour' },
  { label: 'On-call',     to: '/behaviour/on-call',    roles: ALL_SCHOOL_ROLES, module: 'behaviour', behaviourFeature: 'on-call', group: 'behaviour' },
  { label: 'Out Of Lesson', to: '/behaviour/out-of-lesson', roles: ALL_SCHOOL_ROLES, module: 'behaviour', behaviourFeature: 'ool', group: 'behaviour' },
  { label: 'Isolation',   to: '/behaviour/isolation',  roles: ALL_SCHOOL_ROLES, module: 'behaviour', behaviourFeature: 'isolation', group: 'behaviour' },
  { label: 'One-way breach', to: '/behaviour/one-way', roles: ALL_SCHOOL_ROLES, module: 'behaviour', behaviourFeature: 'one-way', group: 'behaviour' },
  { label: 'Morning lates', to: '/behaviour/lates',    roles: ALL_SCHOOL_ROLES, module: 'behaviour', behaviourFeature: 'lates', group: 'behaviour' },
  // "Overview" and "Insights" are synonyms in English, and these two sit next
  // to each other showing different data — what staff logged here, and what the
  // MIS holds. The label says which, so the cross-links are a convenience
  // rather than a repair.
  { label: 'Logged here (7 days)', to: '/behaviour/overview',   roles: ALL_SCHOOL_ROLES, module: 'behaviour', behaviourFeature: 'overview', group: 'behaviour' },
  { label: 'From your MIS',        to: '/behaviour/insights', roles: ['schoolAdmin', 'headteacher', 'lineManager'], module: 'behaviour', group: 'behaviour' },
  // My work
  { label: 'Performance Management', to: '/reviews', roles: ['headteacher', 'lineManager', 'staff'], module: 'pm', group: 'mywork' },
  { label: 'CPD Log',     to: '/cpd',               roles: ALL_SCHOOL_ROLES, module: 'cpd', group: 'mywork' },
  { label: 'My leave',    to: '/leave',             roles: ALL_SCHOOL_ROLES, module: 'leave', group: 'mywork' },
  // Cover-team membership comes from leaveConfig, not a role, so nav cannot tell
  // who belongs — everyone with the module sees the link and the page itself
  // explains it to anyone who is not on the team.
  { label: 'Absence & cover', to: '/leave/cover',   roles: ALL_SCHOOL_ROLES, module: 'leave', group: 'mywork' },
  { label: 'My Timetable', to: '/timetable',        roles: ALL_SCHOOL_ROLES, group: 'mywork' },
  { label: 'Wellbeing',   to: '/wellbeing',         roles: ALL_SCHOOL_ROLES, module: 'wellbeing', group: 'mywork' },
  { label: 'Voice',       to: '/voice',             roles: ALL_SCHOOL_ROLES, module: 'wellbeing', group: 'mywork' },
  // Leadership
  // Not plain 'Performance Management' — that is the My work entry (/reviews),
  // and a headteacher sees both groups.
  { label: 'Performance Management cycles', to: '/cycles', roles: ['schoolAdmin', 'headteacher'], module: 'pm', group: 'leadership' },
  { label: 'Assessment Insights', to: '/insights',  roles: ['schoolAdmin', 'headteacher'], module: 'insights', group: 'leadership' },
  { label: 'Attendance',  to: '/attendance',        roles: ['schoolAdmin', 'headteacher', 'lineManager'], group: 'leadership' },
  { label: 'Data pack',   to: '/reports',           roles: ['schoolAdmin', 'headteacher', 'lineManager'], group: 'leadership' },
  { label: 'Wellbeing trends', to: '/wellbeing/dashboard', roles: ['schoolAdmin', 'headteacher'], module: 'wellbeing', group: 'leadership' },
  { label: 'Pupil wellbeing', to: '/wellbeing/students', roles: ['schoolAdmin', 'headteacher'], module: 'wellbeing', group: 'leadership' },
  { label: 'Surveys',     to: '/voice/surveys',     roles: ['schoolAdmin', 'headteacher', 'lineManager'], module: 'wellbeing', group: 'leadership' },
  { label: 'Feedback',    to: '/voice/feedback',    roles: ['schoolAdmin', 'headteacher', 'lineManager'], module: 'wellbeing', group: 'leadership' },
  { label: 'KS4 Options', to: '/options/manage',    roles: ['schoolAdmin', 'headteacher', 'lineManager'], module: 'options', group: 'leadership' },
  { label: 'Policy compliance', to: '/policies/compliance', roles: ['schoolAdmin', 'headteacher', 'lineManager'], module: 'policies', group: 'leadership' },
  // School
  { label: 'Policies',    to: '/policies',          roles: ALL_SCHOOL_ROLES, module: 'policies', group: 'school' },
  { label: 'Calendar',    to: '/calendar',          roles: ALL_SCHOOL_ROLES, group: 'school' },
  { label: 'Documents',   to: '/documents',         roles: ALL_SCHOOL_ROLES, group: 'school' },
  { label: 'Roster',      to: '/roster',            roles: ALL_SCHOOL_ROLES, group: 'school' },
  // Admin & setup

  // SEND & EHCP. The register carries each pupil's SEND status, which is
  // disability data, so it is hidden from anyone the SENCo has not named —
  // sendCompliance, not a role, because a SENCo is usually a class teacher.
  // Every teacher, for their own pupils. The listener only returns what the
  // rule lets them read, so the page is safe for anyone with the module.
  { label: 'Support summaries', to: '/send/passports', roles: ALL_SCHOOL_ROLES, module: 'send', group: 'mywork' },
  { label: 'My provision', to: '/send/my-provision', roles: ALL_SCHOOL_ROLES, module: 'send', group: 'mywork' },
  { label: 'Review contributions', to: '/send/contributions', roles: ALL_SCHOOL_ROLES, module: 'send', group: 'mywork' },
  { label: 'SEND register', to: '/send', roles: ALL_SCHOOL_ROLES, module: 'send', sendCompliance: true, group: 'send' },
  { label: 'Provision map', to: '/send/provision', roles: ALL_SCHOOL_ROLES, module: 'send', sendCompliance: true, group: 'send' },
  { label: 'Annual reviews', to: '/send/reviews', roles: ALL_SCHOOL_ROLES, module: 'send', sendCompliance: true, group: 'send' },
  { label: 'Oversight', to: '/send/oversight', roles: ALL_SCHOOL_ROLES, module: 'send', sendCompliance: true, group: 'send' },
  // The team is the SENCo's to change (setSendTeam refuses the admin once a
  // SENCo exists), and a SENCo is usually plain staff with no Settings entry —
  // so the SEND section of Settings gets its own way in, here in their group.
  // The admin reaches the same section from Settings.
  { label: 'SEND setup', to: '/school/settings/send', roles: ['headteacher', 'lineManager', 'staff'], module: 'send', sendSenco: true, group: 'send' },

  // Staff Absence. A Bradford score is a colleague's sickness record, so both
  // blades are hidden from anyone the school admin has not named — a list,
  // not a role, and the admin is not on it unless they name themselves.
  { label: 'Bradford scores', to: '/staff-absence/bradford', roles: ALL_SCHOOL_ROLES, module: 'staffAbsence', staffAbsenceViewer: true, staffAbsenceMeasure: 'bradford', group: 'staffAbsence' },
  { label: 'Absence overview', to: '/staff-absence/review-points', roles: ALL_SCHOOL_ROLES, module: 'staffAbsence', staffAbsenceViewer: true, staffAbsenceMeasure: 'reviewPoints', group: 'staffAbsence' },
  { label: 'Returns to work', to: '/staff-absence/returns', roles: ALL_SCHOOL_ROLES, module: 'staffAbsence', staffAbsenceViewer: true, group: 'staffAbsence' },
  { label: 'Absence calendar', to: '/staff-absence/calendar', roles: ALL_SCHOOL_ROLES, module: 'staffAbsence', staffAbsenceViewer: true, group: 'staffAbsence' },
  // Module setup lives in Settings, one section per module, so the admin's
  // menu is people (Staff Roster) and configuration (Settings) — nothing else.
  { label: 'Staff Roster', to: '/school/staff',    roles: ['schoolAdmin'], group: 'admin' },
  { label: 'Settings',     to: '/school/settings', roles: ['schoolAdmin'], group: 'admin' },
];

const ROLE_LABELS: Record<UserRole, string> = {
  superAdmin:  'Super Admin',
  trustAdmin:  'Trust Admin',
  schoolAdmin: 'School Admin',
  headteacher: 'Headteacher',
  lineManager: 'Line Manager',
  staff:       'Staff',
  student:     'Pupil',
};

// ─── Notification count hook ──────────────────────────────────────────────────

function useActionableCount() {
  const { appUser } = useAuth();
  const { activeModules, schoolLoading } = useSchool();
  const uid      = appUser?.id ?? '';
  const schoolId = appUser?.schoolId ?? '';
  const role     = appUser?.role;

  const isLM    = role === 'lineManager' || role === 'headteacher';
  const isStaff = role === 'staff';
  const isAdmin = role === 'schoolAdmin';

  // Reviews assigned to me as line manager — waiting on me. A line manager
  // can't read a review until it's submitted, so the query must say so or the
  // rules deny it. A school admin can be someone's line manager too; their
  // count still comes from buildUserTodos (below), and this listener is only
  // what tells it to recount when one of them moves. Must stay in step with
  // buildUserTodos.
  const { data: lmReviews, loading: lmReviewsLoading } = useCollection(
    uid && schoolId && (isLM || isAdmin)
      ? query(reviewsCol, where('schoolId', '==', schoolId), where('reviewerId', '==', uid), where('status', 'in', ['submittedByStaff', 'midYearOpen']))
      : null,
    uid,
  );

  // Own reviews — staff, but also LMs/headteachers, who are reviewees in their
  // own right. buildUserTodos lists their self-review, so the badge must too.
  const { data: staffReviews } = useCollection(
    uid && schoolId && (isStaff || isLM)
      ? query(reviewsCol, where('schoolId', '==', schoolId), where('revieweeId', '==', uid))
      : null,
    uid,
  );

  // Anyone can be a leave approver — line manager at the first stage, SLT at the
  // second. pendingApproverUids covers both and is emptied once decided, so this
  // needs no status clause. Must stay in step with buildUserTodos.
  const { data: pendingApprovals } = useCollection(
    uid && schoolId && role !== 'superAdmin' && role !== 'student'
      ? query(leaveRequestsCol, where('pendingApproverUids', 'array-contains', uid))
      : null,
    `${uid}:approvals`,
  );

  // Policies awaiting my acknowledgement — keeps the badge in step with what
  // buildUserTodos shows on Home / To Do.
  const isSchoolUser = !!uid && !!schoolId && role !== 'superAdmin' && role !== 'student';
  const { data: activePolicies } = useCollection(
    isSchoolUser ? query(policiesCol, where('schoolId', '==', schoolId), where('status', '==', 'active')) : null,
    `${schoolId}:navpolicies`,
  );
  const { data: myAcks } = useCollection(
    isSchoolUser ? query(policyAcksCol, where('schoolId', '==', schoolId), where('userId', '==', uid)) : null,
    `${uid}:navacks`,
  );
  // Reviews in a closed cycle are read-only, so they must not be counted —
  // mirrors the same guard in buildUserTodos.
  const { data: schoolCycles } = useCollection(
    uid && schoolId && (isLM || isStaff)
      ? query(cyclesCol, where('schoolId', '==', schoolId))
      : null,
    `${schoolId}:navcycles`,
  );

  const staffCategory = appUser?.staffCategory;

  // A schoolAdmin is neither isLM nor isStaff, so before this they only ever
  // saw leave and policy counts — every cycle task on their To Do page went
  // unannounced, for the one role that unblocks everyone else.
  //
  // Derived from buildUserTodos rather than mirrored here: the mirroring is
  // what let the badge drift from the list in the first place, and admin tasks
  // (a cycle drafted, a deadline passed) do not need sub-second liveness.
  // buildUserTodos already includes leave approvals and policy acks, so this
  // is the whole count, not an addition to `approvals`.
  const [adminTodoCount, setAdminTodoCount] = useState(0);
  // Keyed on the module list's contents, as in HomeTodos: the array is rebuilt
  // each time a SchoolContext document arrives, and each rebuild re-ran this.
  const modulesKey = activeModules.join(',');
  // The one admin task that is theirs to do rather than to chase: a review
  // they line-manage. Confirming it should take it off the badge there and
  // then, as it does for a line manager, so the count is redone whenever one
  // of those reviews changes in a way buildUserTodos would notice.
  const adminReviewerKey = isAdmin
    ? (lmReviews as { id: string; status?: string; midYearReview?: { staffSubmittedAt?: unknown } }[])
      .map((r) => `${r.id}:${r.status}:${r.midYearReview?.staffSubmittedAt ? 1 : 0}`)
      .sort().join('|')
    : '';
  useEffect(() => {
    if (role !== 'schoolAdmin' || !uid || !schoolId) { setAdminTodoCount(0); return; }
    // Wait for the reviewer listener's first answer too, or every load counts twice.
    if (schoolLoading || lmReviewsLoading) return;
    let live = true;
    buildUserTodos(uid, schoolId, role, staffCategory, activeModules)
      .then((r) => { if (live) setAdminTodoCount(r.items.length); })
      .catch(() => { if (live) setAdminTodoCount(0); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, uid, schoolId, staffCategory, modulesKey, schoolLoading, lmReviewsLoading, adminReviewerKey]);

  return useMemo(() => {
    const closedCycleIds = new Set(
      (schoolCycles as (Cycle & { id: string })[])
        .filter((c) => c.status !== 'active')
        .map((c) => c.id),
    );
    const inLiveCycle = (r: unknown) => {
      const cycleId = (r as { cycleId?: string }).cycleId;
      return !cycleId || !closedCycleIds.has(cycleId);
    };
    const ackedVersion = new Map<string, number>();
    (myAcks as PolicyAck[]).forEach((a) => {
      ackedVersion.set(a.policyId, Math.max(ackedVersion.get(a.policyId) ?? 0, a.policyVersion));
    });
    const pendingAcks = (activePolicies as (Policy & { id: string })[]).filter(
      (p) => inAckAudience(p, staffCategory) && (ackedVersion.get(p.id) ?? 0) < p.version,
    ).length;
    const approvals = pendingApprovals.length + pendingAcks;
    // Own actionable reviews — mirrors the own-review section of buildUserTodos.
    const ownCount = staffReviews.filter(inLiveCycle).filter((r) => {
      const rev = r as { status: string; midYearReview?: { staffSubmittedAt?: unknown } };
      if (rev.status === 'notStarted' || rev.status === 'inProgress' || rev.status === 'returnedByLineManager') return true;
      // Mid-year only actionable for staff before they've submitted their comments
      if (rev.status === 'midYearOpen') return !(rev.midYearReview?.staffSubmittedAt);
      return false;
    }).length;
    if (isLM) {
      const teamCount = lmReviews.filter(inLiveCycle).filter((r) => {
        const rev = r as { status: string; revieweeId?: string; midYearReview?: { staffSubmittedAt?: unknown }; id?: string };
        // Skip reviews where this LM is also the reviewee (counted in ownCount)
        if (rev.revieweeId === uid) return false;
        if (rev.status === 'submittedByStaff') return true;
        // Mid-year only actionable for LM after staff has submitted their comments
        if (rev.status === 'midYearOpen') return !!(rev.midYearReview?.staffSubmittedAt);
        return false;
      }).length;
      return teamCount + ownCount + approvals;
    }
    if (isStaff) return ownCount + approvals;
    if (role === 'schoolAdmin') return adminTodoCount;
    return approvals;
  }, [isLM, isStaff, role, adminTodoCount, lmReviews, staffReviews, schoolCycles, pendingApprovals, activePolicies, myAcks, staffCategory, uid]);
}

// ─── Notification bell ───────────────────────────────────────────────────────

/**
 * Notification types that ask the recipient to do something, each mirroring a
 * `buildUserTodos` item:
 *
 *   review_submitted   → confirm-review      review_returned    → returned-review
 *   mid_year_open      → mid-year-staff      leave_requested    → leave-approval
 *   policy_ack_required → policy-ack
 *
 * The actionable count already represents them, so the bell must not add them
 * again. Everything else is news — a confirmation, an approval, a cover
 * change — and still counts.
 */
const ACTION_NOTIFICATION_TYPES = new Set([
  // Maps 1:1 to the 'send-contribution' To Do item — never double-counted.
  'send_contribution_requested',
  'send_contribution_chased',
  'send_review_due',
  'send_la_overdue',
  'review_submitted',
  'review_returned',
  'mid_year_open',
  // Each matches a To Do item: a reopened review is "Confirm …'s annual
  // review" again, submitted mid-year comments are the line manager's
  // check-in item, and sent-back ones are the staff member's.
  'review_reopened',
  'mid_year_submitted',
  'mid_year_returned',
  'leave_requested',
  // Also maps to a leave-approval To Do item, so it must be excluded from the
  // unread count or an escalated request is counted twice.
  'leave_escalated',
  'policy_ack_required',
]);

const NOTIF_TYPE_ICONS: Record<string, string> = {
  send_contribution_requested: '🧠',
  send_contribution_chased: '🧠',
  send_review_due: '🧠',
  send_la_overdue: '🧠',
  review_submitted: '📋',
  review_returned:  '↩️',
  review_confirmed: '✅',
  mid_year_open:    '📅',
  mid_year_submitted: '📅',
  mid_year_returned:  '↩️',
  mid_year_complete:  '✅',
  review_reopened:       '🔓',
  review_reopened_staff: '🔓',
  leave_requested:  '🗓️',
  leave_approved:   '✅',
  leave_declined:   '❌',
  leave_to_slt:     '⏭️',
  leave_escalated:  '⏫',
  leave_withdrawn:  '↩️',
  policy_review_due:   '📜',
  policy_ack_required: '📜',
};

function NotificationBell({
  uid, panelPosition = 'right-0 top-11', actionableCount = 0,
}: { uid: string; panelPosition?: string; actionableCount?: number }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: rawNotifs } = useCollection<AppNotification>(
    uid
      ? query(notificationsCol, where('userId', '==', uid), orderBy('createdAt', 'desc'), limit(20))
      : null,
    uid,
  );

  const notifs = rawNotifs as (AppNotification & { id: string })[];
  const unread = notifs.filter((n) => !n.read).length;
  // The To Do nav item (and its badge) is hidden, so the bell carries both
  // signals: unread notifications AND outstanding actions. Without this there
  // is nothing anywhere outside Home telling you something needs doing.
  //
  // But these two notification types ask for the SAME work `actionableCount`
  // already counts, so adding both counted every live task twice — a line
  // manager with five things to do read "9+". Count only the notifications
  // that carry news rather than a task: a confirmation still badges, while
  // "please confirm X" is left to the actionable count that represents it.
  const unreadInformational = notifs.filter(
    (n) => !n.read && !ACTION_NOTIFICATION_TYPES.has(n.type),
  ).length;
  const badgeCount = unreadInformational + actionableCount;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markRead = async (notif: AppNotification & { id: string }) => {
    if (notif.read) return;
    await updateDoc(doc(db, 'notifications', notif.id), { read: true });
  };

  const markAllRead = async () => {
    await Promise.all(notifs.filter((n) => !n.read).map((n) => updateDoc(doc(db, 'notifications', n.id), { read: true })));
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
        aria-label="Notifications"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {badgeCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-0.5 text-[10px] font-bold text-white leading-none">
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}
      </button>

      {open && (
        <div className={`absolute ${panelPosition} z-50 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white shadow-xl overflow-hidden`}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="text-xs font-medium text-brand-600 hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Outstanding actions sit above the notification feed — and this is
              now the only way to reach the full To Do list from outside Home. */}
          {actionableCount > 0 && (
            <Link
              to="/todo"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 border-b border-gray-100 bg-amber-50 px-4 py-3 transition-colors hover:bg-amber-100"
            >
              <span className="text-lg leading-none shrink-0" aria-hidden>📋</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium leading-snug text-gray-900">
                  {actionableCount} thing{actionableCount === 1 ? '' : 's'} need
                  {actionableCount === 1 ? 's' : ''} your attention
                </p>
                <p className="mt-0.5 text-xs leading-snug text-gray-500">Open your To Do list</p>
              </div>
            </Link>
          )}

          {notifs.length === 0 ? (
            <div className={`px-4 text-center text-sm text-gray-400 ${actionableCount > 0 ? 'py-6' : 'py-8'}`}>
              No notifications yet.
            </div>
          ) : (
            <ul className="max-h-96 overflow-y-auto divide-y divide-gray-50">
              {notifs.map((n) => (
                <li key={n.id}>
                  <Link
                    to={n.link ?? `/reviews/${n.reviewId}`}
                    onClick={() => { void markRead(n); setOpen(false); }}
                    className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${n.read ? 'opacity-60' : ''}`}
                  >
                    <span className="text-lg leading-none mt-0.5 shrink-0">{NOTIF_TYPE_ICONS[n.type] ?? '🔔'}</span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm leading-snug ${n.read ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>{n.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5 leading-snug">{n.body}</p>
                    </div>
                    {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-brand-500 shrink-0" />}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Layout ───────────────────────────────────────────────────────────────────

export default function Layout() {
  const { appUser, signOut } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwResetState, setPwResetState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  // Tracks immediate local acceptance so the gate closes without waiting for Firestore snapshot
  const [legalAcceptedLocally, setLegalAcceptedLocally] = useState(false);
  const actionableCount = useActionableCount();

  const { school, hasModule, activeBehaviourFeatures, isMorningLateOfficer, sendTier, isStaffAbsenceViewer, staffAbsenceMeasure } = useSchool();

  const visibleItems = NAV_ITEMS.filter(
    (item) =>
      appUser &&
      item.roles.includes(appUser.role) &&
      (!item.module || hasModule(item.module)) &&
      (!item.behaviourFeature || activeBehaviourFeatures.includes(item.behaviourFeature)) &&
      // Morning lates is only usable by designated officers — hide the blade otherwise.
      (item.behaviourFeature !== 'lates' || isMorningLateOfficer) &&
      // SEND compliance pages are for the named team and leadership only.
      (!item.sendCompliance || sendTier !== 'classroom') &&
      // The SEND setup entry is the named SENCo's way into Settings › SEND.
      (!item.sendSenco || sendTier === 'senco') &&
      // Staff absence reports are for the named viewer list only.
      (!item.staffAbsenceViewer || isStaffAbsenceViewer) &&
      // One measure in the menu: the one the school's policy uses.
      (!item.staffAbsenceMeasure || item.staffAbsenceMeasure === staffAbsenceMeasure),
  );

  // Nav groups are an accordion: on load only the section holding the current
  // page is open, and opening one closes whichever was open. Each navigation
  // re-derives it, so landing on a page from Home opens its section and Home
  // itself (a pinned item, in no section) leaves everything closed. Nothing is
  // remembered between loads — a fresh page is a tidy sidebar.
  const currentGroup = useMemo<string | null>(() => {
    // Same matching NavLink uses: exact for the platform hub, prefix otherwise;
    // longest match wins, so /school/settings/send belongs to the SENCo's SEND
    // group entry rather than Settings when both are visible.
    let best: NavItem | null = null;
    for (const item of visibleItems) {
      const exact = item.to === '/admin';
      const hit = exact ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (hit && (!best || item.to.length > best.to.length)) best = item;
    }
    return best && best.group !== 'pinned' && best.group !== 'platform' ? best.group : null;
  }, [pathname, visibleItems]);
  const [openGroup, setOpenGroup] = useState<string | null>(currentGroup);
  useEffect(() => { setOpenGroup(currentGroup); }, [currentGroup]);
  const toggleGroup = (key: string) => setOpenGroup((cur) => (cur === key ? null : key));
  const pinnedItems = visibleItems.filter((i) => i.group === 'pinned' || i.group === 'platform');
  const renderNavItem = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.to === '/admin'}
      className={({ isActive }) =>
        `flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors ${
          isActive ? 'bg-brand-700 text-white' : 'text-brand-100 hover:bg-brand-800'
        }`
      }
      onClick={() => setMobileOpen(false)}
    >
      <span>{item.label}</span>
      {item.to === '/todo' && actionableCount > 0 && (
        <span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white">
          {actionableCount > 99 ? '99+' : actionableCount}
        </span>
      )}
    </NavLink>
  );

  // ⌘K / Ctrl-K command palette
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPaletteOpen((o) => !o); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  // Auto sign-out after a period of inactivity, with a 1-minute warning. The
  // duration lives in sessionTimeout.ts so the behaviour and all legal/privacy
  // copy stay in lockstep — change it there, not here.
  useIdleTimeout(
    () => { setShowIdleWarning(false); void handleSignOut(); },
    IDLE_TIMEOUT_MS,
    !!appUser,
    () => setShowIdleWarning(true),
    IDLE_WARNING_MS,
    () => setShowIdleWarning(false),
  );

  const handleChangePassword = async () => {
    if (!appUser?.email || pwResetState === 'sending') return;
    setPwResetState('sending');
    try {
      const fns = getFunctions(app, 'europe-west2');
      const resetFn = httpsCallable<{ email: string }, { success: boolean }>(fns, 'requestPasswordReset');
      await resetFn({ email: appUser.email });
      setPwResetState('sent');
    } catch {
      setPwResetState('error');
    }
  };

  return (
    <div className="fixed inset-0 flex overflow-hidden bg-gray-50 print:static print:block print:overflow-visible print:bg-white">
      {/* Sidebar */}
      <aside
        className={`print:hidden ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-40 flex w-56 flex-col bg-brand-900 text-white transition-transform md:relative md:translate-x-0`}
      >
        <div className="flex h-16 items-center px-4 border-b border-brand-700">
          {school?.branding?.logoUrl
            ? <img src={school.branding.logoUrl} alt="School crest" className="h-9 w-9 rounded-md bg-white/90 object-contain p-0.5" />
            : <LogoIcon variant="bars" size={28} theme="light" />}
        </div>

        {/* School name — prominent, below logo */}
        {school && (
          <div className="px-4 py-3 border-b border-brand-800">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-400 mb-0.5">School</p>
            <p className="text-sm font-semibold text-white leading-snug">{school.branding?.displayName || school.name}</p>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {/* Search / command palette hidden at user request 2026-06-22 — kept for
              future reuse (CommandPalette.tsx + ⌘K handler remain wired). See vault
              note 'schoolone-search-feature-hidden' before re-adding. */}
          {pinnedItems.map(renderNavItem)}
          {NAV_GROUPS.map((g) => {
            const items = visibleItems.filter((i) => i.group === g.key);
            if (items.length === 0) return null;
            const isCollapsed = openGroup !== g.key;
            return (
              <div key={g.key} className="mt-3 border-t border-brand-800 pt-3">
                <button
                  type="button"
                  onClick={() => toggleGroup(g.key)}
                  className="flex w-full items-center justify-between px-3 pb-1 text-left text-[11px] font-bold uppercase tracking-[0.14em] text-brand-500 transition-colors hover:text-brand-300"
                >
                  <span>{g.label}</span>
                  <svg className={`h-3 w-3 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {!isCollapsed && <div className="mt-0.5 space-y-0.5 pl-2">{items.map(renderNavItem)}</div>}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-brand-700 px-4 py-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-medium text-brand-300 truncate flex-1 min-w-0">
              {appUser?.displayName}
            </p>
            {appUser && <NotificationBell uid={appUser.id} panelPosition="left-0 bottom-11" actionableCount={actionableCount} />}
          </div>
          <p className="text-xs text-brand-400 mb-2">
            {appUser ? ROLE_LABELS[appUser.role] : ''}
          </p>
          <div className="flex items-center gap-3 mb-2">
            <Link
              to="/faq"
              className="text-xs text-brand-300 hover:text-white transition-colors"
            >
              Help &amp; FAQ
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => void handleChangePassword()}
              disabled={pwResetState === 'sending'}
              className="text-xs text-brand-300 hover:text-white transition-colors disabled:opacity-50"
            >
              {pwResetState === 'sending' ? 'Sending…' : 'Change password'}
            </button>
            <span className="text-brand-600 text-xs">·</span>
            <button
              onClick={() => void handleSignOut()}
              className="text-xs text-brand-300 hover:text-white transition-colors"
            >
              Sign out
            </button>
          </div>
          {pwResetState === 'sent' && (
            <p className="mt-1.5 text-xs text-green-400">
              Reset link sent to your email.
            </p>
          )}
          {pwResetState === 'error' && (
            <p className="mt-1.5 text-xs text-red-400">
              Failed to send. Please try again.
            </p>
          )}
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Idle warning modal */}
      {showIdleWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4 text-center">
            <div className="text-4xl">⏱</div>
            <h2 className="text-lg font-semibold text-gray-900">Still there?</h2>
            <p className="text-sm text-gray-500">
              You'll be signed out in <strong>1 minute</strong> due to inactivity.
            </p>
            <button
              type="button"
              onClick={() => setShowIdleWarning(false)}
              className="btn-primary w-full"
            >
              Stay signed in
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-1 flex-col min-w-0 overflow-hidden print:block print:overflow-visible">
        {/* Top bar (mobile) */}
        <header className="flex h-16 items-center gap-3 border-b border-gray-200 bg-white px-4 md:hidden print:hidden">
          <button
            onClick={() => setMobileOpen(true)}
            className="text-gray-500 hover:text-gray-700 shrink-0"
            aria-label="Open menu"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          {school?.branding?.logoUrl
            ? <img src={school.branding.logoUrl} alt="" className="h-6 w-6 rounded object-contain" />
            : <LogoIcon variant="bars" size={24} theme="dark" />}
          {school && (
            <>
              <span className="text-gray-200 text-sm">|</span>
              <span className="text-sm font-semibold text-gray-700 truncate">{school.branding?.displayName || school.name}</span>
            </>
          )}
          <div className="ml-auto shrink-0">
            {appUser && <NotificationBell uid={appUser.id} actionableCount={actionableCount} />}
          </div>
        </header>

        {/* Legal gate — shown to school admins until they accept the current version of ToS + DPA */}
        {appUser?.role === 'schoolAdmin' && school && !legalAcceptedLocally && (!school.legalAcceptedAt || school.legalVersion !== CURRENT_LEGAL_VERSION) ? (
          <LegalGate
            schoolId={appUser.schoolId ?? ''}
            schoolAdminId={appUser.id}
            schoolAdminName={appUser.displayName}
            onAccepted={() => setLegalAcceptedLocally(true)}
          />
        ) : (
          <main className="flex-1 overflow-y-auto p-4 sm:p-6 print:overflow-visible print:p-0">
            <Outlet />
          </main>
        )}
      </div>

      <CommandPalette
        items={visibleItems.map((i) => ({ label: i.label, to: i.to }))}
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />

      {/* On every signed-in page: the moment someone hits a rough edge is the
          moment to catch the suggestion, on the page it happened. */}
      <FeedbackButton />
    </div>
  );
}
